import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { PartnerCenterEnrollmentStatus } from "@gamedev-web/one-gaming-crm";
import { HeroButton } from "@gaming/xds-react-js";

import * as commonStyles from "../../../components/commonStyles/commonStyles.module.css";
import { DevResource, DevResourceItem } from "../../../components/DevResource";
import { FormattedText } from "../../../components/FormattedText";
import {
  appDeveloperAgreement,
  GdkDocsUrl,
  GdkDownloadUrl,
  LatestGdkDownloadUrl,
  partnerCenterDashboardUrl,
  storeContentPoliciesUrl,
  XboxDevForumsUrl,
  XboxGdkSamplesUrl,
  XboxPublisherGuideUrl,
} from "../../../constants";
import { getImage, ImageType } from "../../../helpers/imageHelper";
import { useStrings } from "../../../localization/localizationHelpers";
import { RouteNames } from "../../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../../partnerConfig/routes";
import { xboxOnboardActions } from "../../../redux/actions/xboxOnboardActions";
import { getXboxOnboardData } from "../../../redux/selectors/xboxOnboardSelectors";
import * as styles from "../XboxOnboardPage.module.css";

export const XboxOnboardNdaSigned = () => {
  const dispatch = useDispatch();
  const data = useSelector(getXboxOnboardData);
  const partnerCenterEnrollmentStatus =
    data?.partnerCenterEnrollmentStatus ?? PartnerCenterEnrollmentStatus.Unset;

  if (
    partnerCenterEnrollmentStatus === null ||
    partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.Unset
  ) {
    return null;
  }

  return (
    <div className={styles.enrollmentSection}>
      {partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.Enabled && (
        <XboxOnboardEnrollEnabled dispatch={dispatch} />
      )}
      {(partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.InProgress ||
        partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.EarlyInProgress) && (
        <XboxOnboardEnrollInProgress />
      )}
      {(partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.Complete ||
        partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.EarlyComplete) && (
        <XboxOnboardEnrollComplete />
      )}
      {(partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.Rejected ||
        partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.EarlyRejected) && (
        <XboxOnboardEnrollRejected />
      )}
    </div>
  );
};

const XboxOnboardEnrollEnabled = ({ dispatch }: { dispatch: ReturnType<typeof useDispatch> }) => {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const [pageTitle, pageDescription, termsCheckboxPrefix, appDeveloperAgreementText, enroll] =
    useStrings(
      "idXboxGdkPageTitle",
      "idXboxGdkPageDescription",
      "idXboxGdkTermsCheckboxPrefix",
      "idXboxGdkAppDeveloperAgreement",
      "idXboxGdkEnroll"
    );

  const handleEnroll = () => {
    dispatch(xboxOnboardActions.startEnrollment.start());
  };

  return (
    <>
      <h2 className={styles.enrollmentTitle}>{pageTitle}</h2>
      <div className={styles.sectionBox}>
        <p className={styles.enrollmentDescription}>{pageDescription}</p>
        <div className={styles.termsSection}>
          <label className={styles.termsLabel}>
            <input
              type="checkbox"
              checked={isTermsAccepted}
              onChange={e => setIsTermsAccepted(e.target.checked)}
              className={styles.termsCheckbox}
            />
            <span>
              {termsCheckboxPrefix}{" "}
              <a
                href={appDeveloperAgreement}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.termsLink}
              >
                {appDeveloperAgreementText}
              </a>
            </span>
          </label>
        </div>
        <div className={styles.enrollmentActions}>
          <HeroButton
            as="button"
            className={commonStyles.actionButton}
            onClick={handleEnroll}
            disabled={!isTermsAccepted}
          >
            {enroll}
          </HeroButton>
        </div>
      </div>
    </>
  );
};

const XboxOnboardEnrollInProgress = () => {
  const [isWaitingMode, setIsWaitingMode] = useState(true);

  const [inProgressTitle, inProgressMessage, checkEnrollmentButton] = useStrings(
    "idXboxGdkInProgressTitle",
    "idXboxGdkInProgressMessage",
    "idXboxGdkCheckEnrollment"
  );

  useEffect(() => {
    const timer = setTimeout(() => setIsWaitingMode(false), 180000);
    return () => clearTimeout(timer);
  }, []);

  const onCheckEnrollment = () => {
    window.location.reload();
  };

  return (
    <>
      <h2 className={styles.enrollmentTitle}>{inProgressTitle}</h2>
      <div className={styles.sectionBox}>
        <p className={styles.enrollmentDescription}>
          <FormattedText text={inProgressMessage} />
        </p>
        {isWaitingMode && (
          <div className={styles.enrollmentSpinner}>
            <XboxSpinner />
          </div>
        )}
        {!isWaitingMode && (
          <div className={styles.enrollmentActions}>
            <HeroButton
              as="button"
              className={commonStyles.actionButton}
              onClick={onCheckEnrollment}
            >
              {checkEnrollmentButton}
            </HeroButton>
          </div>
        )}
      </div>
    </>
  );
};

const XboxOnboardEnrollComplete = () => {
  const [
    completeTitle,
    completeDescription,
    visitPartnerCenter,
    downloadGdkFile,
    developmentResources,
    latestGdkDownload,
    gamePublishingGuide,
    gdkDocs,
    techCommunityForums,
    gdkSamples,
    microsoftStorePolicies,
    programsTitle,
    programsSubtitle,
    idXboxTitle,
    idXboxDescription,
    idXboxAction,
    pitchTitle,
    pitchDescription,
    pitchAction,
  ] = useStrings(
    "idXboxGdkCompleteTitle",
    "idXboxGdkCompleteDescription",
    "idXboxGdkVisitPartnerCenter",
    "idXboxGdkDownloadGdkFile",
    "idXboxGdkDevelopmentResources",
    "idXboxGdkLatestGdkDownload",
    "idXboxGdkGamePublishingGuide",
    "idXboxGdkGdkDocs",
    "idXboxGdkTechCommunityForums",
    "idXboxGdkGdkSamples",
    "idXboxGdkMicrosoftStorePolicies",
    "xboxOnboardProgramsTitle",
    "xboxOnboardProgramsSubtitle",
    "xboxOnboardIdXboxTitle",
    "xboxOnboardIdXboxDescription",
    "xboxOnboardIdXboxAction",
    "xboxOnboardPitchTitle",
    "xboxOnboardPitchDescription",
    "xboxOnboardPitchAction"
  );

  const developmentResourceLinks: DevResourceItem[] = [
    { title: latestGdkDownload, url: LatestGdkDownloadUrl },
    { title: gdkDocs, url: GdkDocsUrl },
    { title: gdkSamples, url: XboxGdkSamplesUrl },
    { title: gamePublishingGuide, url: XboxPublisherGuideUrl },
    { title: techCommunityForums, url: XboxDevForumsUrl },
    { title: microsoftStorePolicies, url: storeContentPoliciesUrl },
  ];

  return (
    <>
      <h2 className={styles.enrollmentTitle}>{completeTitle}</h2>
      <div className={styles.sectionBox}>
        <p className={styles.enrollmentDescription}>{completeDescription}</p>

        <ol className={styles.instructionsList}>
          <li className={styles.instructionItem}>
            <FormattedText text={visitPartnerCenter.replace("{0}", partnerCenterDashboardUrl)} />
          </li>
          <li className={styles.instructionItem}>
            <FormattedText text={downloadGdkFile.replace("{0}", GdkDownloadUrl)} />
          </li>
        </ol>

        <div className={styles.resourcesSection}>
          <h3 className={styles.resourcesTitle}>{developmentResources}</h3>
          <div className={styles.resourcesGrid}>
            {developmentResourceLinks.map((item, index) => (
              <DevResource key={index} item={item} />
            ))}
          </div>
        </div>
      </div>

      <h2 className={styles.enrollmentTitle}>{programsTitle}</h2>
      <p className={styles.programsSubtitle}>{programsSubtitle}</p>
      <div className={styles.programsGrid}>
        <div className={styles.programCard}>
          <img
            className={styles.programCardImage}
            src={getImage("idxboxlogo", ImageType.Png)}
            alt={idXboxTitle}
          />
          <div className={styles.programCardContent}>
            <h3 className={styles.programCardTitle}>{idXboxTitle}</h3>
            <p className={styles.programCardDescription}>{idXboxDescription}</p>
            <HeroButton
              as="a"
              href={OnboardingUrls[RouteNames.IdXboxWelcome]}
              className={commonStyles.actionButton}
            >
              {idXboxAction}
            </HeroButton>
          </div>
        </div>
        <div className={styles.programCard}>
          <img
            className={styles.programCardImage}
            src={getImage("pitchlogo", ImageType.Png)}
            alt={pitchTitle}
          />
          <div className={styles.programCardContent}>
            <h3 className={styles.programCardTitle}>{pitchTitle}</h3>
            <p className={styles.programCardDescription}>{pitchDescription}</p>
            <HeroButton
              as="a"
              href={OnboardingUrls[RouteNames.PitchWelcome]}
              className={commonStyles.actionButton}
            >
              {pitchAction}
            </HeroButton>
          </div>
        </div>
      </div>
    </>
  );
};

const XboxOnboardEnrollRejected = () => {
  const [rejectedTitle, rejectedContent] = useStrings(
    "idXboxGdkRejectedTitle",
    "idXboxGdkRejectedContent"
  );

  return (
    <>
      <h2 className={styles.enrollmentTitle}>{rejectedTitle}</h2>
      <div className={styles.sectionBox}>
        <FormattedText text={rejectedContent} />
      </div>
    </>
  );
};
